// Pure rekenfuncties voor de Uitrusting-credits (bestelmodule).
//
// Géén DB-toegang en géén berekeningen in routes of crons — alle credit-saldo-
// en kwartaal-afleiding komt hier samen. Stijl volgt uren-calculations /
// sick-leave-calculations: pure functies, datum-afleiding via Intl in
// Europe/Amsterdam, geen date-fns/dayjs (bewuste zero-dep keuze, CLAUDE.md).
//
// Kernregels van de module (vast):
//   - 350 credits per medewerker per kwartaal, NIET stapelbaar (350 = max).
//   - Vervalt bij de volgende kwartaaltoekenning (geen overdracht).
//   - Beschikbaar = toegekend - gebruikt - gereserveerd.
//
// Tijdzone: de DB slaat UTC op, maar jaar+kwartaal worden ALTIJD in NL-tijd
// afgeleid (Europe/Amsterdam). Rond de kwartaal-/jaargrens is dat kritiek: een
// UTC-`getMonth()` zou rond middernacht NL het verkeerde kwartaal geven.

const TIME_ZONE = 'Europe/Amsterdam'

/**
 * Vast credit-budget per medewerker per kwartaal. NIET stapelbaar (350 = max),
 * vervalt bij de volgende kwartaaltoekenning. Eén bron — geïmporteerd door de
 * cron (`uitrusting-credit-toekenning.ts`) én de service-helper
 * (`services/uitrusting/credits.ts`) zodat het bedrag niet kan divergeren.
 */
export const KWARTAAL_CREDITS = 350

interface YearMonthDay {
  jaar: number
  maand: number // 1..12
  dag: number // 1..31
}

/**
 * Jaar/maand/dag van een instant in Europe/Amsterdam, onafhankelijk van de
 * process-TZ (Docker draait vaak op UTC). `en-CA` levert een ISO-achtig
 * `YYYY-MM-DD`-formaat zodat parsing triviaal is.
 */
function ymdInAmsterdam(date: Date): YearMonthDay {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = fmt.formatToParts(date)
  const get = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? NaN)
  return { jaar: get('year'), maand: get('month'), dag: get('day') }
}

/** Eerste maand (1-indexed) van een kwartaal: Q1→1, Q2→4, Q3→7, Q4→10. */
function eersteMaandVanKwartaal(kwartaal: number): number {
  return (kwartaal - 1) * 3 + 1
}

/**
 * Offset (in minuten) van Europe/Amsterdam t.o.v. UTC op een gegeven instant.
 * Positief = vóór UTC (CET = +60, CEST = +120). Werkt door de wandkloktijd in
 * NL te vergelijken met de UTC-componenten van hetzelfde instant.
 */
function amsterdamOffsetMinuten(date: Date): number {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(date)
  const get = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? NaN)
  // `hour` kan in sommige runtimes '24' zijn om middernacht — normaliseer naar 0.
  const hour = get('hour') % 24
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'))
  return Math.round((asUtc - date.getTime()) / 60000)
}

/**
 * Beschikbare credits = toegekend - gebruikt - gereserveerd.
 *
 * Bewust NIET geclampt op 0: een negatieve uitkomst kan niet by-construction
 * ontstaan (reserveren wordt geblokkeerd boven het saldo), dus een negatief
 * getal signaleert een boekhoudfout die we niet willen verbergen.
 */
export function beschikbareCredits(saldo: {
  toegekend: number
  gebruikt: number
  gereserveerd: number
}): number {
  return saldo.toegekend - saldo.gebruikt - saldo.gereserveerd
}

/**
 * Huidig jaar + kwartaal (1..4), afgeleid in Europe/Amsterdam.
 *
 * Rond de jaar-/kwartaalgrens telt NL-tijd: `2026-12-31T23:30:00Z` is al
 * 00:30 NL op 2027-01-01 → {2027, 1}.
 */
export function huidigKwartaal(now: Date = new Date()): { jaar: number; kwartaal: number } {
  const { jaar, maand } = ymdInAmsterdam(now)
  const kwartaal = Math.floor((maand - 1) / 3) + 1
  return { jaar, kwartaal }
}

/**
 * Vorig kwartaal. Q1 wrapt naar Q4 van het vorige jaar; anders zelfde jaar,
 * kwartaal - 1.
 */
export function vorigKwartaal(
  jaar: number,
  kwartaal: number,
): { jaar: number; kwartaal: number } {
  if (kwartaal === 1) return { jaar: jaar - 1, kwartaal: 4 }
  return { jaar, kwartaal: kwartaal - 1 }
}

/**
 * UTC-instant van 00:00 Europe/Amsterdam op de eerste dag van het kwartaal.
 *
 * Robuust over de CET/CEST-overgang: we nemen de naïeve UTC-middernacht van de
 * lokale datum als startgok, lezen de werkelijke NL-offset op dat instant en
 * trekken die eraf. Eén iteratie volstaat omdat een kwartaal nooit op de
 * DST-overgangsnacht (laatste zondag maart/oktober) begint, dus de offset van
 * de gok en van het eindresultaat zijn gelijk.
 *
 * Gebruikt door de cron om actieve medewerkers te filteren (endDate >= start).
 */
export function kwartaalStartUTC(jaar: number, kwartaal: number): Date {
  const maand = eersteMaandVanKwartaal(kwartaal)
  // Naïeve UTC-middernacht van de lokale datum als startgok.
  const guess = new Date(Date.UTC(jaar, maand - 1, 1, 0, 0, 0))
  const offsetMin = amsterdamOffsetMinuten(guess)
  // Lokaal 00:00 = UTC 00:00 - offset (offset positief = NL vóór UTC).
  return new Date(guess.getTime() - offsetMin * 60000)
}

/**
 * Credit-vrije categorieën: PBM en VEILIGHEID tellen NIET mee in het credit-
 * budget. Een regel uit zo'n categorie reserveert/boekt geen credits en blokkeert
 * het indienen nooit (ook bij saldo 0/negatief). Eén bron zodat API + portal
 * dezelfde regel hanteren.
 */
export const CREDIT_VRIJE_CATEGORIEEN = ['PBM', 'VEILIGHEID'] as const

/**
 * True als de categorie credit-vrij is (PBM/VEILIGHEID). null/undefined of een
 * onbekende categorie telt mee (niet credit-vrij), zodat oude bestelregels zonder
 * categorie hun bestaande gedrag houden.
 */
export function isCreditVrijeCategorie(categorie: string | null | undefined): boolean {
  return categorie === 'PBM' || categorie === 'VEILIGHEID'
}

/**
 * Credit-belastend totaal van een bestelling: som van aantal × creditsPerStuk
 * over alléén de niet-credit-vrije regels. PBM/VEILIGHEID-regels tellen niet mee
 * (zie isCreditVrijeCategorie). Dit is het bedrag dat gereserveerd/afgeboekt
 * wordt en waartegen het saldo wordt gecheckt; een pure PBM/VEILIGHEID-bestelling
 * levert 0 op (altijd indienbaar).
 */
export function berekenCreditsTotaal(
  regels: ReadonlyArray<{ aantal: number; creditsPerStuk: number; categorie?: string | null }>,
): number {
  return regels.reduce(
    (som, r) => (isCreditVrijeCategorie(r.categorie) ? som : som + r.aantal * r.creditsPerStuk),
    0,
  )
}

// ─── Credit → euro (FO-072) ──────────────────────────────────────────────────

/**
 * Vaste credit→euro-conversie: 1 credit = 1 euro. Eén bron voor de 1:1-regel
 * (FO-072, antwoord opdrachtgever) zodat geen enkele plek z'n eigen omrekening
 * verzint; verandert de verhouding ooit, dan alleen hier (en de regression-test
 * faalt bewust).
 *
 * Let op: dit is puur de EURO-DUIDING van een credit-waarde in de kostentelling.
 * Het credit-saldo zelf (CreditSaldo/CreditMutatie, het kwartaalbudget) blijft
 * in credits geboekt — deze constante raakt de saldo-/reserveringslogica niet.
 * Een budget van KWARTAAL_CREDITS (350) credits = € 350 aan inkoopwaarde.
 */
export const EURO_PER_CREDIT = 1

/**
 * Euro-waarde van een credit-bedrag (1 credit = 1 euro). Afgerond op 2 decimalen
 * (eurocenten) zodat de uitkomst direct als geldbedrag telt en geen IEEE-754-ruis
 * meedraagt. Pure functie, geen DB. Niet-eindige invoer (NaN/Infinity) → 0, zodat
 * een kapotte credit-waarde de telling niet vergiftigt.
 *
 * Gebruikt als TERUGVAL in de kostentelling wanneer een uitrusting-regel (nog)
 * geen euro-prijs (`prijsExclBtw`) heeft; de euro-prijs houdt altijd voorrang.
 */
export function creditsNaarEuro(credits: number): number {
  if (!Number.isFinite(credits)) return 0
  return Math.round(credits * EURO_PER_CREDIT * 100) / 100
}
